import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CenterFocusStrongOutlinedIcon from "@mui/icons-material/CenterFocusStrongOutlined";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import {
  H_GAP,
  layoutForest,
  linkPath,
  NODE_H,
  NODE_W,
} from "./chartLayout";
import { initialsOf, T, TYPE_WORDS } from "./ui";

// The org chart surface: pan, zoom, collapse, select.
//
// TWO DELIBERATE VISUAL RULES, both from direct feedback that the earlier
// screens were "too many boxes and borders" and "generic":
//
//   1. No node has a border. Depth is carried by fill, weight and size, and
//      elevation by shadow. A ring appears only on the selected node.
//   2. Links are curves, and the chain from the selected node up to its
//      department lights up. That is what makes a diagram feel like a diagram
//      rather than an indented list drawn with lines.
//
// Rendering is HTML boxes positioned over one SVG layer holding the links.
// Text and avatars in SVG are a fight over fonts and ellipsis; HTML over SVG
// gets both for free, and the layout math is shared by construction.

const MIN_K = 0.35;
const MAX_K = 1.9;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const AvatarStack = ({ members = [], total: totalProp, onDark }) => {
  // `members` may be a PREVIEW — the API caps members_preview at four — so its
  // length is not the size of the team. member_count is the real figure and is
  // passed in as `total`; the array length is only the fallback for callers
  // that have the full list.
  const total = Number.isFinite(totalProp) ? totalProp : members.length;
  const shown = members.slice(0, 3);
  const named = members.map((m) => m.name).filter(Boolean);
  const unnamed = Math.max(0, total - named.length);

  // An unfilled position is worth seeing. Rendering nothing made it
  // indistinguishable from a node that simply does not show people.
  if (total === 0) {
    return (
      <Tooltip title="Nobody assigned yet">
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
          <PersonOutlineRoundedIcon
            sx={{
              fontSize: 14,
              color: onDark ? "rgba(255,255,255,.55)" : T.muted,
              opacity: 0.75,
            }}
          />
          <Typography
            sx={{
              fontFamily: T.font,
              fontSize: 10.5,
              fontWeight: 700,
              color: onDark ? "rgba(255,255,255,.6)" : T.muted,
            }}
          >
            0
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {shown.map((member, idx) => (
        <Tooltip
          key={member.id || member.user_id || idx}
          title={member.name || "Member"}
        >
          <Box
            sx={{
              width: 21,
              height: 21,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              fontFamily: T.font,
              fontSize: 8.5,
              fontWeight: 800,
              letterSpacing: "-0.01em",
              bgcolor: onDark ? "rgba(255,255,255,.22)" : T.accentWash,
              color: onDark ? "#fff" : T.accentDeep,
              // Overlapped and ringed in the surface colour, so the stack reads
              // as one object without any of them needing an outline.
              boxShadow: `0 0 0 2px ${onDark ? T.accent : T.surface}`,
              ml: idx === 0 ? 0 : "-6px",
              zIndex: shown.length - idx,
            }}
          >
            {initialsOf(member.name)}
          </Box>
        </Tooltip>
      ))}
      {/* The TOTAL, not the overflow. "+2" beside three faces made you add up
          to learn a team was five; this says five. */}
      {/* The tooltip can only name whoever was sent. Saying "and 8 more"
          rather than listing four and implying that is everybody. */}
      <Tooltip
        title={
          total === 1
            ? named[0] || "1 person"
            : `${total} people` +
              (named.length ? `: ${named.join(", ")}` : "") +
              (unnamed > 0 ? ` and ${unnamed} more` : "")
        }
      >
        <Typography
          sx={{
            fontFamily: T.font,
            fontSize: 10.5,
            fontWeight: 800,
            color: onDark ? "rgba(255,255,255,.9)" : T.muted,
            ml: 0.7,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {total}
        </Typography>
      </Tooltip>
    </Box>
  );
};

const OrgCanvas = ({
  roots,
  selectedId,
  onSelect,
  collapsed,
  onToggleCollapse,
  onCollapseAll,
  onExpandAll,
  ancestorIds,
  height = 620,
}) => {
  const wrapRef = useRef(null);
  const [view, setView] = useState({ x: 0, y: 0, k: 1 });
  const dragRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const { nodes, links, width, height: contentH } = useMemo(
    () => layoutForest(roots, collapsed),
    [roots, collapsed],
  );

  // Centres the whole forest and scales it to fit, never magnifying past 1:1 —
  // a two-node chart blown up to fill the viewport looks broken.
  const fit = useCallback(() => {
    const box = wrapRef.current?.getBoundingClientRect();
    if (!box || width === 0) return;
    const pad = 48;
    const k = clamp(
      Math.min((box.width - pad) / width, (box.height - pad) / contentH, 1),
      MIN_K,
      MAX_K,
    );
    setView({
      x: (box.width - width * k) / 2,
      y: Math.max((box.height - contentH * k) / 2, 24),
      k,
    });
  }, [width, contentH]);

  // Re-fit when the shape of the tree changes, not on every render.
  useEffect(() => {
    fit();
  }, [fit]);

  const zoomBy = (factor) =>
    setView((prev) => {
      const box = wrapRef.current?.getBoundingClientRect();
      const k = clamp(prev.k * factor, MIN_K, MAX_K);
      if (!box) return { ...prev, k };
      // Zoom about the centre of the viewport so the thing you were looking at
      // stays where it was.
      const cx = box.width / 2;
      const cy = box.height / 2;
      return {
        k,
        x: cx - ((cx - prev.x) / prev.k) * k,
        y: cy - ((cy - prev.y) / prev.k) * k,
      };
    });

  const onWheel = (event) => {
    // Only intercept a deliberate zoom gesture; a plain scroll should still
    // scroll the page the canvas sits on.
    if (!event.ctrlKey && !event.metaKey && Math.abs(event.deltaY) < 24) return;
    event.preventDefault();
    const box = wrapRef.current.getBoundingClientRect();
    const px = event.clientX - box.left;
    const py = event.clientY - box.top;
    setView((prev) => {
      const k = clamp(prev.k * (event.deltaY > 0 ? 0.92 : 1.08), MIN_K, MAX_K);
      return {
        k,
        x: px - ((px - prev.x) / prev.k) * k,
        y: py - ((py - prev.y) / prev.k) * k,
      };
    });
  };

  const onPointerDown = (event) => {
    // Nodes and the control bar handle their own clicks; dragging starts on the
    // background only.
    //
    // The control bar has to be excluded explicitly, and it is not cosmetic:
    // it sits inside this surface, so a press on it bubbles here, and the
    // setPointerCapture below then retargets the whole gesture — pointerup
    // included — onto this wrapper. The button never sees a click, so zoom, fit
    // and expand/collapse all did nothing at all. The press also flipped the
    // cursor to grabbing and panned the chart if the finger moved a pixel.
    if (event.target.closest("[data-org-node], [data-org-controls]")) return;
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: view.x,
      originY: view.y,
    };
    setDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event) => {
    if (!dragRef.current) return;
    const { startX, startY, originX, originY } = dragRef.current;
    setView((prev) => ({
      ...prev,
      x: originX + (event.clientX - startX),
      y: originY + (event.clientY - startY),
    }));
  };

  const endDrag = () => {
    dragRef.current = null;
    setDragging(false);
  };

  const ancestors = ancestorIds || new Set();

  return (
    <Box
      sx={{
        position: "relative",
        height,
        borderRadius: T.radius.lg,
        overflow: "hidden",
        // A faint dot grid that pans with the content: it gives the surface a
        // sense of place, and it is the cheapest way to make panning legible.
        bgcolor: "#f3f7f8",
        backgroundImage: `radial-gradient(${T.accentLine} 1px, transparent 1px)`,
        backgroundSize: `${22 * view.k}px ${22 * view.k}px`,
        backgroundPosition: `${view.x}px ${view.y}px`,
        cursor: dragging ? "grabbing" : "grab",
        touchAction: "none",
      }}
      ref={wrapRef}
      onWheel={onWheel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerLeave={endDrag}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})`,
          transformOrigin: "0 0",
          width,
          height: contentH,
        }}
      >
        <svg
          width={width}
          height={contentH}
          style={{ position: "absolute", inset: 0, overflow: "visible" }}
          aria-hidden="true"
        >
          {links.map(({ from, to, childId }) => {
            // The chain from the selected node up to its department is drawn
            // heavier, so where you are is readable without reading any labels.
            const lit = ancestors.has(childId);
            return (
              <path
                key={`${from.node.id}-${childId}`}
                d={linkPath(from, to)}
                fill="none"
                stroke={lit ? T.accent : "#cddfe2"}
                strokeWidth={lit ? 2.2 : 1.4}
                strokeLinecap="round"
              />
            );
          })}
        </svg>

        {nodes.map(({ node, depth, x, y }) => {
          const isRoot = depth === 0;
          const isSelected = selectedId === node.id;
          const onPath = ancestors.has(node.id);
          const kids = node.children || [];
          const isCollapsed = collapsed.has(node.id);
          const members = node.members || node.members_preview || [];
          const openJobs = node.job_count ?? 0;
          const needsSetup =
            !isRoot && (node.template_count ?? 0) === 0;

          return (
            <Box key={node.id}>
              <Box
                data-org-node
                role="button"
                tabIndex={0}
                aria-label={`${node.name}${isRoot ? ", department" : `, ${TYPE_WORDS[node.node_type]?.label || "position"}`}`}
                onClick={() => onSelect(node)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect(node);
                  }
                }}
                sx={{
                  position: "absolute",
                  left: x - NODE_W / 2,
                  top: y,
                  width: NODE_W,
                  height: NODE_H,
                  px: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: 0.35,
                  borderRadius: "13px",
                  cursor: "pointer",
                  // No borders anywhere. Depth is fill and weight; elevation is
                  // shadow; selection is a ring.
                  bgcolor: isRoot ? T.accent : T.surface,
                  color: isRoot ? "#fff" : T.ink,
                  boxShadow: isSelected
                    ? `0 0 0 2.5px ${T.accent}, 0 10px 24px rgba(12,138,155,.22)`
                    : onPath
                    ? "0 4px 14px rgba(12,138,155,.16)"
                    : "0 2px 8px rgba(15,23,42,.08)",
                  transition:
                    "box-shadow .18s ease, transform .18s ease, background .18s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: isSelected
                      ? `0 0 0 2.5px ${T.accent}, 0 12px 28px rgba(12,138,155,.26)`
                      : "0 8px 20px rgba(15,23,42,.13)",
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${isRoot ? "#fff" : T.accent}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  {needsSetup && (
                    <Tooltip title="No job template yet">
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: T.warn,
                          flex: "none",
                        }}
                      />
                    </Tooltip>
                  )}
                  <Typography
                    sx={{
                      fontFamily: T.font,
                      fontSize: isRoot ? 14.5 : 13.5,
                      fontWeight: isRoot ? 800 : 700,
                      letterSpacing: isRoot ? "-0.01em" : 0,
                      lineHeight: 1.25,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                    title={node.name}
                  >
                    {node.name}
                  </Typography>
                  {openJobs > 0 && (
                    <Tooltip title={`${openJobs} open job(s)`}>
                      <Box
                        sx={{
                          px: 0.65,
                          py: 0.05,
                          borderRadius: T.radius.pill,
                          bgcolor: isRoot ? "rgba(255,255,255,.24)" : T.okWash,
                          color: isRoot ? "#fff" : T.ok,
                          fontFamily: T.font,
                          fontSize: 10.5,
                          fontWeight: 800,
                          flex: "none",
                        }}
                      >
                        {openJobs}
                      </Box>
                    </Tooltip>
                  )}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography
                    sx={{
                      fontFamily: T.font,
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      color: isRoot ? "rgba(255,255,255,.78)" : T.faint,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isRoot
                      ? "Department"
                      : TYPE_WORDS[node.node_type]?.label || "Position"}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <AvatarStack
                    members={members}
                    total={node.member_count}
                    onDark={isRoot}
                  />
                </Box>
              </Box>

              {/* Collapse handle, sitting on the link below the node — where an
                  org chart puts it, and it doubles as a count of what is
                  hidden. */}
              {kids.length > 0 && (
                <Tooltip
                  title={
                    isCollapsed
                      ? `Show ${kids.length} below`
                      : "Hide what is below"
                  }
                >
                  <Box
                    data-org-node
                    role="button"
                    tabIndex={0}
                    aria-label={
                      isCollapsed ? "Expand this branch" : "Collapse this branch"
                    }
                    onClick={(event) => {
                      event.stopPropagation();
                      onToggleCollapse(node.id);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        event.stopPropagation();
                        onToggleCollapse(node.id);
                      }
                    }}
                    sx={{
                      position: "absolute",
                      left: x - 10,
                      top: y + NODE_H - 9,
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: isCollapsed ? T.accent : T.surface,
                      color: isCollapsed ? "#fff" : T.muted,
                      fontFamily: T.font,
                      fontSize: 10.5,
                      fontWeight: 800,
                      boxShadow: "0 2px 6px rgba(15,23,42,.16)",
                      cursor: "pointer",
                      zIndex: 3,
                      "&:hover": { bgcolor: isCollapsed ? T.accentDark : T.accentWash },
                      "&:focus-visible": { outline: `2px solid ${T.accent}` },
                    }}
                  >
                    {isCollapsed ? kids.length : "–"}
                  </Box>
                </Tooltip>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Controls float over the surface rather than sitting in a toolbar band,
          which would put another horizontal rule on the screen. */}
      <Box
        // Read by onPointerDown, which must not start a pan from here. See the
        // comment there before removing this.
        data-org-controls
        sx={{
          position: "absolute",
          right: 12,
          bottom: 12,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          bgcolor: T.surface,
          borderRadius: T.radius.md,
          p: 0.4,
          boxShadow: "0 4px 14px rgba(15,23,42,.14)",
        }}
      >
        {[
          { icon: <AddIcon sx={{ fontSize: 17 }} />, label: "Zoom in", act: () => zoomBy(1.18) },
          { icon: <RemoveIcon sx={{ fontSize: 17 }} />, label: "Zoom out", act: () => zoomBy(0.85) },
          {
            icon: <CenterFocusStrongOutlinedIcon sx={{ fontSize: 17 }} />,
            label: "Fit to screen",
            act: fit,
          },
          {
            icon: <UnfoldLessIcon sx={{ fontSize: 17 }} />,
            label: "Collapse all",
            act: onCollapseAll,
          },
          {
            icon: <UnfoldMoreIcon sx={{ fontSize: 17 }} />,
            label: "Expand all",
            act: onExpandAll,
          },
        ].map((control) => (
          <Tooltip key={control.label} title={control.label} placement="left">
            <IconButton
              size="small"
              aria-label={control.label}
              onClick={control.act}
              sx={{ color: T.muted, "&:hover": { color: T.accent } }}
            >
              {control.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Box>

      <Typography
        sx={{
          position: "absolute",
          left: 14,
          bottom: 12,
          fontFamily: T.font,
          fontSize: 11.5,
          color: T.faint,
          pointerEvents: "none",
          userSelect: "none",
        }}
      >
        Drag to pan · ⌘/Ctrl + scroll to zoom · click a box for details
      </Typography>
    </Box>
  );
};

export default OrgCanvas;

// Re-exported so the page can size spacing against the same constants the
// layout uses, instead of guessing at magic numbers.
export { NODE_W, NODE_H, H_GAP };
